import type { DropResult } from '@hello-pangea/dnd'
import { DragDropContext, Draggable, Droppable } from '@hello-pangea/dnd'
import type { CheckboxItem } from '@vedalib/editor/lib/elements'
import { textToRtValue } from '@vedalib/rich-text'
import type { RichTextValue } from '@vedalib/rich-text'
import * as R from 'ramda'
import { useCallback, useMemo, useState } from 'react'

import AbsolutePortal from 'components/Editor/components/AbsolutePortal/AbsolutePortal'
import RichText from 'components/form/RichText/RichText'
import { useGetRichTextProps } from 'components/form/RichText/useGetRichTextProps'
import { IconButton } from 'components/uiKit/Button'
import { Tooltip } from 'components/uiKit/Dropdown'
import Icon from 'components/uiKit/Icon'
import { KitSize } from 'components/uiKit/KitTypes'
import { PreviewMode } from 'services/Store/Project/enums'
import { t } from 'services/Translation'
import { extendTestData } from 'utils/test/qaData'

import type { CheckboxElementType } from '../checkboxTypes'
import CheckboxContainerStyled from '../styled/CheckboxContainerStyled'
import DragIconStyled from '../styled/DragIconStyled'
import DraggableStyled from '../styled/DraggableStyled'
import RichTextContainer from '../styled/RichTectContainer'
import Checkbox from './Checkbox'

const CheckboxElementEditor: CheckboxElementType = ({
  element,
  mode,
  styles,
  waiting,
  onChange,
  font,
  state,
  setState,
}) => {
  const [isDragging, setIsDragging] = useState(false)
  const { id: elementId, value } = element
  const { items } = value
  const disabled = items.length < 2
  const stateItems = useMemo(() => state?.items || [], [state?.items])

  const { isActiveElement, activeIndex, onLabelSelect, isFill } = useGetRichTextProps({
    elementId,
    mode,
  })

  const onChangeLabel = useCallback(
    (labelValue: RichTextValue, index: number) =>
      onChange?.(R.assocPath(['items', index, 'label'], labelValue, value)),
    [onChange, value],
  )

  const handleDelete = useCallback(
    (item: CheckboxItem) => () =>
      onChange?.(
        R.assoc(
          'items',
          items.filter(({ value: id }) => id !== item.value),
          value,
        ),
      ),
    [items, onChange, value],
  )

  const handleMove = useCallback(
    ({ destination, source }: DropResult) => {
      if (destination) {
        onChange?.(R.assoc('items', R.move(source.index, destination.index, items), value))
      }
      setIsDragging(false)
    },
    [items, onChange, value],
  )

  const handleSetValid = useCallback(
    (index: number, checked: boolean) => () => {
      const newItems = { items: [...stateItems] }
      newItems.items[index] = R.assoc('checked', checked, newItems.items[index])
      setState?.(newItems)
    },
    [setState, stateItems],
  )

  return (
    <DragDropContext onDragEnd={handleMove} onDragStart={() => setIsDragging(true)}>
      <Droppable droppableId={elementId}>
        {(provided) => (
          <CheckboxContainerStyled
            ref={provided.innerRef}
            {...provided.droppableProps}
            $style={styles}
          >
            {stateItems.map((item, index) => (
              <Draggable draggableId={item.value} index={index} key={item.value}>
                {(provided) => (
                  <DraggableStyled
                    ref={provided.innerRef}
                    {...provided.draggableProps}
                    key={item.value}
                    style={provided.draggableProps.style}
                  >
                    <Checkbox
                      checked={item.checked}
                      setIsValid={handleSetValid(index, !item.checked)}
                      styles={styles}
                      testData={extendTestData({
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        label: item?.label ? (item?.label[0] as any).children[0].text : '',
                        index,
                      })}
                      value={item.value}
                      isEditor
                    >
                      <RichTextContainer
                        $checked={item.checked}
                        $disabled={item.disabled}
                        onMouseDown={() => onLabelSelect(index.toString())}
                      >
                        <RichText
                          active={isActiveElement && activeIndex === index.toString()}
                          disabled={!isFill}
                          isPreview={mode?.previewMode !== PreviewMode.editor}
                          name={`checkboxItem.${index}`}
                          onChange={(val) => onChangeLabel(val, index)}
                          styles={font}
                          value={item?.label || textToRtValue('')}
                          waiting={waiting}
                        />
                      </RichTextContainer>
                    </Checkbox>
                    {!disabled && isFill && isActiveElement && (
                      <DragIconStyled {...provided.dragHandleProps} $style={styles}>
                        <Icon name='builderDrugAndDrop' size={KitSize.XS} />
                      </DragIconStyled>
                    )}
                    {isFill && isActiveElement && !disabled && !isDragging && (
                      <AbsolutePortal
                        name='deleteMultipleChoiceItem'
                        placement='right'
                        translateX='3px'
                        translateY='-50%'
                      >
                        <Tooltip overlay={t('uiKit.tooltip.deleteAnswer')}>
                          <IconButton
                            icon='otherClose'
                            name='deleteMultipleChoiceItem'
                            onClick={handleDelete(item)}
                            size={KitSize.XS}
                            styleType='secondary'
                          />
                        </Tooltip>
                      </AbsolutePortal>
                    )}
                  </DraggableStyled>
                )}
              </Draggable>
            ))}
            {provided.placeholder}
          </CheckboxContainerStyled>
        )}
      </Droppable>
    </DragDropContext>
  )
}

export default CheckboxElementEditor
